import { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, Eye, EyeOff, Video, X, Save } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminFreeClasses() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", youtube_url: "", thumbnail: "", published: true });
  const [saving, setSaving] = useState(false);

  const fetchClasses = async () => {
    try {
      const { data } = await api.get(`/admin/free-classes?search=${search}`);
      setClasses(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClasses(); }, [search]);

  const resetForm = () => {
    setForm({ title: "", description: "", youtube_url: "", thumbnail: "", published: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (cls) => {
    setForm({ title: cls.title, description: cls.description || "", youtube_url: cls.youtube_url, thumbnail: cls.thumbnail || "", published: cls.published });
    setEditing(cls.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/free-classes/${editing}`, form);
      } else {
        await api.post("/admin/free-classes", form);
      }
      resetForm();
      fetchClasses();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this free class?")) return;
    try {
      await api.delete(`/admin/free-classes/${id}`);
      fetchClasses();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  const togglePublish = async (cls) => {
    try {
      await api.put(`/admin/free-classes/${cls.id}`, { published: !cls.published });
      fetchClasses();
    } catch (err) {
      alert("Failed to update");
    }
  };

  const inputClass = "w-full bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-free-classes-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Free Classes</h1>
          <button data-testid="add-free-class-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0284C7] text-white px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#0369A1] transition-none">
            <Plus size={16} /> Add Class
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-3 text-slate-400" />
          <input
            data-testid="search-free-classes"
            type="text"
            placeholder="Search free classes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#0284C7]"
          />
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="free-class-form-modal">
            <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">{editing ? "Edit Free Class" : "Add Free Class"}</h2>
                <button onClick={resetForm} className="text-slate-400"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Title *</label>
                  <input data-testid="fc-title" type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required maxLength={200} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Description</label>
                  <textarea data-testid="fc-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-20 resize-none`} maxLength={1000} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">YouTube Video URL *</label>
                  <input data-testid="fc-youtube-url" type="url" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className={inputClass} placeholder="https://youtube.com/watch?v=... or https://youtu.be/..." required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Thumbnail URL (optional)</label>
                  <input data-testid="fc-thumbnail" type="url" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className={inputClass} placeholder="https://..." />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input data-testid="fc-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#0284C7]" />
                  <span className="text-sm font-medium">Published (visible to students)</span>
                </label>
                <button data-testid="fc-submit" type="submit" disabled={saving} className="w-full bg-[#0284C7] text-white font-bold py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                  <Save size={16} /> {saving ? "Saving..." : editing ? "Update Class" : "Add Class"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-10 text-slate-400 text-sm">Loading...</div>
        ) : classes.length === 0 ? (
          <div className="text-center py-16">
            <Video size={48} className="mx-auto text-slate-200 mb-3" />
            <p className="text-sm text-slate-500">No free classes yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {classes.map((cls) => (
              <div key={cls.id} data-testid={`fc-item-${cls.id}`} className={`bg-white border p-4 flex items-center gap-3 ${cls.published ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
                <div className="w-10 h-10 bg-[#0284C7]/10 flex items-center justify-center flex-shrink-0 rounded">
                  <Video size={18} className="text-[#0284C7]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{cls.title}</p>
                  <p className="text-[10px] text-slate-400 truncate">{cls.youtube_url}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 ${cls.published ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-400"}`}>
                  {cls.published ? "LIVE" : "DRAFT"}
                </span>
                <button data-testid={`fc-toggle-${cls.id}`} onClick={() => togglePublish(cls)} className="text-slate-400 hover:text-[#0284C7] p-1">
                  {cls.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button data-testid={`fc-edit-${cls.id}`} onClick={() => handleEdit(cls)} className="text-slate-400 hover:text-[#0284C7] p-1">
                  <Edit2 size={16} />
                </button>
                <button data-testid={`fc-delete-${cls.id}`} onClick={() => handleDelete(cls.id)} className="text-slate-300 hover:text-red-500 p-1">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
